<script setup lang="ts">
// One search bar for the support views (participants, trainers, projects). They each had their own:
// some had a search button, some only reacted to Enter, the hint text was reused from unrelated keys.
// This unifies the look and interaction — an input plus a search button, Enter also searches, an
// optional hint underneath. URL syncing and what "search" does stay with each view.
import { useI18n } from "vue-i18n";

defineProps({
    modelValue: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    /** Shown in bold under the field; omit for no hint. */
    hint: { type: String, default: "" },
    /** Passed through so existing Cypress selectors (e.g. #trainers-table_input_trainer) keep working. */
    inputId: { type: String, default: undefined },
    buttonId: { type: String, default: undefined },
    loading: { type: Boolean, default: false },
});

defineEmits<{ "update:modelValue": [value: string]; search: [] }>();

const { t } = useI18n();
</script>

<template>
    <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
            <InputText
                :id="inputId"
                :model-value="modelValue"
                :placeholder="placeholder"
                class="w-full"
                @update:model-value="$emit('update:modelValue', $event ?? '')"
                @keyup.enter="$emit('search')"
            />
            <Button
                :id="buttonId"
                icon="pi pi-search"
                :loading="loading"
                :aria-label="t('buttons.find')"
                v-tooltip="t('buttons.find')"
                @click="$emit('search')"
            />
        </div>
        <small v-if="hint">
            <b>{{ hint }}</b>
        </small>
    </div>
</template>
